import { useState, useEffect } from 'react';
import { getDatabase, ref, onValue, off, query, orderByChild, equalTo } from 'firebase/database';
import app from '../../services/firebase';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { Typography, Box, Button, Card, CardContent, Select, MenuItem, InputLabel, FormControl } from '@mui/material';
import '../../styles/mobidawg-style.scss';

function AdminContactRegistry() {
    const [debugMode] = useState(import.meta.env.VITE_DEBUG_MODE === 'true');
    const auth = getAuth(app);
    const navigate = useNavigate();
    const database = getDatabase(app);
    const [contactRegistryState, setContactRegistryState] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterProjectReview, setFilterProjectReview] = useState('New'); // Default filter
    const [sortBy, setSortBy] = useState('dtAdded'); // Default sort by timestamp

    useEffect(() => {
        const fetchData = async () => {
            try {
                const user = auth.currentUser; 
                if (!user) { 
                    navigate('/auth/signon'); // Redirect to sign-on if not logged in
                    return; 
                }

                // Force a token refresh to get the latest custom claims
                const idTokenResult = await user.getIdTokenResult(true);
                const isAdmin = idTokenResult.claims.admin;

                if (!isAdmin) {
                    setError("Access Denied: You do not have administrator privileges.\nRedirecting to home page...");
                    setLoading(false);
                    // Optional: Redirect to home page after a delay
                    setTimeout(() => navigate('/'), 10000);
                    return;
                }


                // Reference to the contactRegistry node
                const contactRegistryRef = ref(database, 'contactRegistry');
                let currentQuery = query(contactRegistryRef);

                // Apply filter if selected
                if (filterProjectReview && filterProjectReview !== 'All') {
                    currentQuery = query(currentQuery, orderByChild('projectReview'), equalTo(filterProjectReview));
                } else {
                    // If no specific filter, we still need an orderByChild for consistent querying
                    // Firebase requires an orderByChild when using equalTo, so if we remove equalTo,
                    // we should ensure a default orderByChild is present if we want to sort.
                    // For 'All' or no filter, we can order by timestamp directly.
                    currentQuery = query(currentQuery, orderByChild(sortBy));
                }
                
                // Note: Firebase Realtime Database queries can only order by one child directly.
                // If you need complex multi-field sorting/grouping, you might need to fetch and sort client-side.
                // For now, we'll sort by dtAdded descending if no specific projectReview filter is applied.
                // If projectReview is filtered, it implicitly groups by projectReview.

                const unsubscribe = onValue(currentQuery, (snapshot) => {
                    const data = snapshot.val();
                    if (data) {
                        let contactRegistry = Object.keys(data).map(key => ({
                            id: key, // Firebase key as ID
                            ...data[key]
                        }));

                        // Client-side sorting for dtAdded descending
                        if (sortBy === 'dtAdded') {
                            contactRegistry.sort((a, b) => b.dtAdded - a.dtAdded);
                        }
                        // Add other sorting logic here if needed for other fields

                        setContactRegistryState(contactRegistry);
                    } else {
                        setContactRegistryState([]);
                    }
                    setLoading(false);
                }, (err) => {
                    setError(err.message);
                    setLoading(false);
                    console.error("Error fetching data:", err);
                });

                return () => {
                    off(currentQuery, 'value', unsubscribe);
                    console.log("Unmounting AdminContactRegistry listener");
                };

            } catch (err) {
                console.log("Error:", err);
                setError("Failed to retrieve contacts");
                setLoading(false);
            }
        };

        fetchData();
    }, [database, auth, filterProjectReview, sortBy]); // Re-run effect when filter or sort changes

    const handleCardClick = (contactItem) => {
        navigate('/admin/contactresponseform', { state: { contactItem } });
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
                Admin Project Registry List
            </Typography>

            <FormControl fullWidth margin="normal">
                <InputLabel id="project-review-filter-label">Filter by Project Review</InputLabel>
                <Select
                    labelId="project-review-filter-label"
                    value={filterProjectReview}
                    label="Filter by Project Review"
                    onChange={(e) => setFilterProjectReview(e.target.value)}
                >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="New">New</MenuItem>
                    <MenuItem value="In Progress">In Progress</MenuItem>
                    <MenuItem value="Completed">Completed</MenuItem>
                    <MenuItem value="Archived">Archived</MenuItem>
                </Select>
            </FormControl>

            {/* Add sorting control if needed, e.g., for sorting within a filtered group */}
            {/* <FormControl fullWidth margin="normal">
                <InputLabel id="sort-by-label">Sort By</InputLabel>
                <Select
                    labelId="sort-by-label"
                    value={sortBy}
                    label="Sort By"
                    onChange={(e) => setSortBy(e.target.value)}
                >
                    <MenuItem value="dtAdded">Timestamp (Newest First)</MenuItem>
                    <MenuItem value="projectTitle">Project Title</MenuItem>
                </Select>
            </FormControl> */}

            {loading ? (
                <Typography>Loading Project Registry...</Typography>
            ) : error ? (
                <Typography style={{ color: 'red' }}>Error: {error}</Typography>
            ) : (
                contactRegistryState.length > 0 ? (
                    contactRegistryState.map((contactRegistry) => (
                        <Card 
                            key={contactRegistry.id} 
                            sx={{ 
                                mb: 2, 
                                cursor: 'pointer',
                                '&:hover': {
                                    boxShadow: 6,
                                },
                            }}
                            onClick={() => handleCardClick(contactRegistry)}
                        >
                            <CardContent sx={{ 
                                display: 'flex',
                                flexDirection: 'column',
                                backgroundImage: 'conic-gradient(from 90deg,rgb(205, 236, 200) 10%,rgb(177, 205, 172) 80%,rgb(205, 236, 200) 29% )',  
                                backgroundPosition: 'left top', 
                                backgroundRepeat: 'no-repeat', 
                                backgroundSize: 'cover',
                                opacity: 0.7,
                                padding: 0,
                                mb: 0 
                            }}>
                                <Typography component="body1" gutterBottom sx={{ fontSize: '1.0rem', m: '0.5rem' }}>
                                    <b>Name:</b> {contactRegistry.firstname} {contactRegistry.lastname}
                                </Typography>
                                <Typography component="body1" gutterBottom sx={{ fontSize: '1.0rem', m: '0.5rem' }}>
                                    <b>Email:</b> {contactRegistry.email}
                                </Typography>
                                <Typography component="body1" gutterBottom sx={{ fontSize: '1.0rem', m: '0.5rem' }}>
                                    <b>Project Title:</b> {contactRegistry.projectTitle}
                                </Typography>
                                <Typography component="body1" gutterBottom sx={{ fontSize: '1.0rem', m: '0.5rem' }}>
                                    <b>Review Status:</b> {contactRegistry.projectReview}
                                </Typography>
                                <Typography component="body1" gutterBottom sx={{ fontSize: '1.0rem', ml: '0.5rem', mr: '0.5rem' }}>
                                    <b>Submitted:</b> {new Date(contactRegistry.dtAdded).toLocaleString()}
                                </Typography>
                                {debugMode && ( 
                                    <Typography component="caption1" gutterBottom sx={{ fontSize: '0.8rem', mt: '2rem' }}>
                                        ID: {contactRegistry.id} (UID: {contactRegistry.uid})
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    ))
                ) : (
                    <Typography variant="body1" sx={{ mt: 4, textAlign: 'center' }}>
                        No project requests found with the current filter.
                    </Typography>
                )
            )}
        </Box>
    );
}

export default AdminContactRegistry;